import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Copy, Check, Users } from 'lucide-react';
import { isValidWord } from '../../data/words';
import { encodeWord } from '../../utils/challengeUtils';

export function ChallengeModal({ open, onOpenChange }) {
  const [word, setWord] = useState('');
  const [error, setError] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleWordChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5);
    setWord(value);
    setError('');
    setGeneratedLink('');
    setCopied(false);
  };

  const generateLink = () => {
    if (word.length !== 5) {
      setError('Word must be exactly 5 letters');
      return;
    }
    
    if (!isValidWord(word)) {
      setError('Not a valid word');
      return;
    }

    const encoded = encodeWord(word);
    const baseUrl = window.location.origin + window.location.pathname;
    const link = `${baseUrl}?challenge=${encoded}`;
    setGeneratedLink(link);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = generatedLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = (isOpen) => {
    if (!isOpen) {
      setWord('');
      setError('');
      setGeneratedLink('');
      setCopied(false);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white border-gray-200 text-black max-w-sm rounded-lg" data-testid="challenge-modal" aria-describedby="challenge-description">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center flex items-center justify-center gap-2">
            <Users className="w-5 h-5" />
            Challenge a Friend
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p id="challenge-description" className="text-sm text-gray-600 text-center">
            Pick a word and send the link to a friend. They'll try to guess your word!
          </p>
          
          {/* Word Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Enter a 5-letter word</label>
            <div className="flex gap-1 justify-center">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="w-12 h-12 border-2 border-gray-300 rounded flex items-center justify-center text-xl font-bold uppercase"
                >
                  {word[i] || ''}
                </div>
              ))}
            </div>
            <input
              type="text"
              value={word}
              onChange={handleWordChange}
              placeholder="Type your word..."
              className="w-full px-3 py-2 border border-gray-300 rounded text-center uppercase font-bold tracking-widest focus:outline-none focus:border-[#6aaa64]"
              maxLength={5}
              data-testid="challenge-word-input"
            />
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
          </div>

          {/* Generate Button */}
          {!generatedLink && (
            <Button
              onClick={generateLink}
              disabled={word.length !== 5}
              className="w-full bg-[#6aaa64] hover:bg-[#5a9a54] text-white rounded"
              data-testid="generate-link-button"
            >
              Generate Challenge Link
            </Button>
          )}

          {/* Generated Link */}
          {generatedLink && (
            <div className="space-y-3">
              <div className="p-3 bg-gray-100 rounded text-xs break-all text-gray-600">
                {generatedLink}
              </div>
              <Button
                onClick={copyLink}
                className={`w-full rounded transition-all ${
                  copied 
                    ? 'bg-[#6aaa64] hover:bg-[#6aaa64] text-white' 
                    : 'bg-black hover:bg-gray-800 text-white'
                }`}
                data-testid="copy-link-button"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </>
                )}
              </Button>
              <p className="text-xs text-gray-500 text-center">
                Share this link with your friend. The word is hidden!
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
